﻿import { useState, useEffect } from "react";
import { cloneDeep, isEqual } from "lodash";
import { IMaterialAuditResult } from "../../interfaces";

export const useMaterialAuditGroup = (materialAuditResults?: IMaterialAuditResult[], readonly?: boolean) => {
    const [sort, setSort] = useState(['material.warehouse.value', 'material.rack', 'material.row', 'material.pallet']);
    const [reverseSort, setReverseSort] = useState(false);
    const [modelArray, setModelArray] = useState<IMaterialAuditResult[][]>([]);

    const onUpdateMaterialAuditResult = (materialAuditResult: IMaterialAuditResult, removeResult?: boolean) => {
        let updatedAuditResults = cloneDeep(materialAuditResults) || [];

        updatedAuditResults = updatedAuditResults.filter(x =>
            x.material.materialId !== materialAuditResult.material.materialId);

        if (!removeResult) {
            updatedAuditResults.push(materialAuditResult);
        }

        return updatedAuditResults;
    };

    const getAuditGroups = (materials?: IMaterialAuditResult[]) => {
        if (!materials) return [];

        const groupedArrays: { [key: string]: IMaterialAuditResult[] } = {};

        for (const object of materials) {
            const category = (readonly ? object.materialStatic?.site?.id : object.material.site?.id) ?? "Site Unknown";

            if (!groupedArrays[category]) {
                groupedArrays[category] = [];
            }

            groupedArrays[category].push(object);
        }

        // Sort each array based on the specified properties
        for (const category in groupedArrays) {
            const materialsArray = groupedArrays[category];
            materialsArray.sort((a, b) => {
                const aConcatenatedValue = getConcatenatedValue(a, sort);
                const bConcatenatedValue = getConcatenatedValue(b, sort);

                // Check if all values can be parsed as numbers
                const allValuesAreNumbers = [aConcatenatedValue, bConcatenatedValue].every(value =>
                    typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value)) && Number.isFinite(Number(value)))
                );

                if (allValuesAreNumbers) {
                    return parseFloat(aConcatenatedValue) - parseFloat(bConcatenatedValue);
                } else {
                    return aConcatenatedValue.localeCompare(bConcatenatedValue);
                }
            });

            if (reverseSort) {
                materialsArray.reverse();
            }
        }

        return Object.values(groupedArrays);
    };

    const getConcatenatedValue = (object: any, properties: string[] | undefined): string => {
        if (!properties || properties.length === 0) {
            return "";
        }
        return properties.map(property => getProperty(object, property)).join(" ");
    };

    const getProperty = (object: any, property: string): any => {
        const properties = property.split('.');
        let value = object;
        for (const prop of properties) {
            if (value === null || value === undefined) {
                return null;
            }
            value = value[prop];
        }
        return value;
    };

    const handleSort = (requested: string[]) => {
        if (isEqual(requested, sort)) {
            setReverseSort(!reverseSort);
        } else {
            setReverseSort(false);
            setSort(requested);
        }
    };

    const handleGroupSort = (grouped: IMaterialAuditResult[][]) => {
        grouped.sort((a, b) => {
            const valueA = a[0].material?.site?.value;
            const valueB = b[0].material?.site?.value;

            if (valueA == null && valueB == null) {
                return 0;
            } else if (valueA == null) {
                return -1;
            } else if (valueB == null) {
                return 1;
            } else {
                if (valueA < valueB) {
                    return -1;
                } else if (valueA > valueB) {
                    return 1;
                } else {
                    return 0;
                }
            }
        });
    };

    useEffect(() => {
        let grouped = getAuditGroups(materialAuditResults);
        handleGroupSort(grouped);
        setModelArray(grouped);
    }, [sort, reverseSort, materialAuditResults?.length]);

    return {
        modelArray,
        handleSort,
        onUpdateMaterialAuditResult,
    };
};